import { ReactComponent as ImportDataSvg } from "@/components/ui/icons/import-data.svg";
import { useTrackElementSizes } from "@/hooks/use-track-element-sizes";
import { selectModeName } from "@/store/mode-selectors";
import { selectFlyWalkSceneFilter } from "@/store/modes/flywalk-mode-slice";
import { useAppSelector } from "@/store/store-hooks";
import { selectActiveTool } from "@/store/ui/ui-selectors";
import { ToolName } from "@/store/ui/ui-slice";
import { useIsReportToolActive } from "@/store/ui/ui-utils";
import { SceneFilter } from "@/types/scene-filter";
import { FaroButton, FaroSvgIcon, FaroText, grey } from "@faro-lotv/flat-ui";
import { useIntercom } from "@faro-lotv/foreign-observers";
import { Box, Stack } from "@mui/material";
import {
  useCallback,
  useEffect,
  useState,
  type JSX,
  type ReactNode,
} from "react";
import { useUiOverlayContext } from "../common/ui-overlay-provider";
import { useActiveCamera } from "../r3f/cameras/active-camera-context";
import { AnalysisToolBarTool } from "./analysis-toolbar/analysis-tool";
import { CameraSwitch } from "./camera-switch";
import { CanvasToolbar } from "./canvas-toolbar";
import { ModeSwitch } from "./mode-switch";
import { useTreeContext } from "./tree/tree-context";
import { ViewSettingsToolbar } from "./view-settings-toolbar/view-settings-toolbar";

/**
 * @returns Overlay to render for all modes
 */
export function AllModesOverlay(): JSX.Element {
  const activeTool = useAppSelector(selectActiveTool);

  const currMode = useAppSelector(selectModeName);
  const {
    cameraProjection,
    setCameraProjection,
    isProjectionSwitchVisible,
    predefinedView,
    setPredefinedView,
  } = useActiveCamera();

  // Currently Gaussian Splats do not support orthographic projection
  const isViewingGaussianSplats =
    useAppSelector(selectFlyWalkSceneFilter) === SceneFilter.GaussianSplats;

  const shouldShowSmartNavigationToggle = currMode === "overview";

  const shouldShowProjectionOption =
    currMode === "overview" &&
    isProjectionSwitchVisible &&
    !isViewingGaussianSplats;

  const shouldShowAnalysisToolBar =
    activeTool !== ToolName.clipScene &&
    activeTool !== ToolName.surfaceWand &&
    (currMode === "overview" || currMode === "walk");
  const analysisExpanded = useAppSelector(
    (state) =>
      state.pointCloudAnalysisTool.panelTab === "clashes" &&
      (state.ui.activeTool === ToolName.analysis ||
        (!!state.pointCloudAnalysisTool.activeAnalysisId &&
          !!state.pointCloudAnalysisTool.showActiveAnalysis)),
  );

  // Communicate the available space in the bottom right to the other mode-specific overlays
  const { setBottomRightAvailableHeight } = useUiOverlayContext();
  const [bottomRightContainer, setBottomRightContainer] = useState<
    HTMLDivElement | undefined
  >();
  const { height } = useTrackElementSizes(bottomRightContainer);
  useEffect(() => {
    setBottomRightAvailableHeight(height);
  }, [height, setBottomRightAvailableHeight]);

  // TODO: remove this once the report and snapshot tools will be their own modes
  const isReportToolActive = useIsReportToolActive();
  const { tree } = useTreeContext();
  const showEmptyPage = isReportToolActive && tree.length === 0;

  return (
    <Box
      component="div"
      sx={{
        width: "100%",
        height: "100%",
      }}
    >
      {showEmptyPage && <EmptyReportToolOverlay />}
      <OverlayToolbars
        bottomLeft={
          <>
            <CameraSwitch
              shouldShowProjectionOption={shouldShowProjectionOption}
              shouldShowSmartNavigationToggle={shouldShowSmartNavigationToggle}
              cameraOptions3D={cameraProjection}
              onCameraChange={setCameraProjection}
              showPredefinedViewsOptions
              predefinedView={predefinedView}
              onPredefinedViewSelected={setPredefinedView}
            />
            <ModeSwitch />
          </>
        }
        topRight={<ViewSettingsToolbar />}
        analysis={shouldShowAnalysisToolBar && <AnalysisToolBarTool />}
        analysisExpanded={analysisExpanded}
        tools={<CanvasToolbar />}
        bottomRightRef={setBottomRightContainer}
      />
    </Box>
  );
}

/** @returns Toolbars constrained to the containing viewer's available height. */
export function OverlayToolbars({
  bottomLeft,
  topRight,
  analysis,
  analysisExpanded = false,
  tools,
  bottomRightRef,
}: {
  bottomLeft?: ReactNode;
  topRight: ReactNode;
  analysis?: ReactNode;
  analysisExpanded?: boolean;
  tools: ReactNode;
  bottomRightRef?(container: HTMLDivElement | undefined): void;
}): JSX.Element {
  return (
    <Stack
      direction="row"
      sx={{
        justifyContent: "space-between",
        width: "100%",
        height: "100%",
        p: 2,
        position: "relative",
        boxSizing: "border-box",
        pointerEvents: "none",
      }}
    >
      <Stack
        direction="column"
        spacing="1em"
        sx={{ justifyContent: "end", "& > *": { pointerEvents: "auto" } }}
      >
        {bottomLeft}
      </Stack>

      <Stack
        direction="column"
        sx={{
          height: "100%",
          alignSelf: "center",
          gap: 1,
          justifyContent: "space-between",
        }}
      >
        <Stack
          direction="column"
          sx={{ flexBasis: 0, flexGrow: 1, "& > *": { pointerEvents: "auto" } }}
        >
          {topRight}
        </Stack>
        <Stack direction="row" sx={{ gap: 2, minHeight: 0 }}>
          {analysis && !analysisExpanded && (
            <Stack
              direction="column"
              sx={{
                justifyContent: "center",
                minHeight: 0,
                "& > *": { pointerEvents: "auto" },
              }}
            >
              {analysis}
            </Stack>
          )}
          <Stack
            direction="column"
            sx={{
              flexBasis: 0,
              flexGrow: 1,
              justifyContent: "center",
              "& > *": { pointerEvents: "auto" },
            }}
          >
            {tools}
          </Stack>
        </Stack>
        {/** box used to ensure the CanvasToolbar is centered in the column */}
        <Box
          ref={bottomRightRef}
          component="div"
          sx={{ flexBasis: 0, flexGrow: 1 }}
        />
      </Stack>
      {analysis && analysisExpanded && (
        <Box
          sx={{
            position: "absolute",
            top: 64,
            bottom: 16,
            right: 78,
            width: 480,
            maxWidth: "calc(100% - 96px)",
            minHeight: 0,
            display: "flex",
            flexDirection: "column",
            "& > *": { pointerEvents: "auto" },
          }}
        >
          {analysis}
        </Box>
      )}
    </Stack>
  );
}

function EmptyReportToolOverlay(): JSX.Element {
  const intercom = useIntercom();
  const openIntercom = useCallback(() => intercom?.showMessenger(), [intercom]);
  const openHelpPage = useCallback(
    () =>
      window.open(
        "https://help.holobuilder.com/en/collections/15503529-waypoints-in-sphere-xg",
        "_blank",
      ),
    [],
  );

  return (
    <Stack
      direction="column"
      sx={{
        width: "100%",
        height: "100%",
        textAlign: "center",
        position: "absolute",
        backgroundColor: grey[0],
        justifyContent: "center",
        alignItems: "center",
        gap: 2.5,
      }}
    >
      {/* Illustration */}
      <FaroSvgIcon
        source={ImportDataSvg}
        aria-label="import data icon"
        sx={{
          width: "300px",
          height: "200px",
        }}
      />
      <FaroText variant="heading32">
        No compatible items in the project
      </FaroText>
      <FaroText variant="heading20" sx={{ color: grey[600] }}>
        This features only supports 360° and 2D photos.
      </FaroText>
      <FaroText variant="heading20" sx={{ color: grey[600] }}>
        To get started, add compatible data to your project.
      </FaroText>
      <Stack direction="column" sx={{ py: "30px", gap: "20px" }}>
        {intercom && (
          <FaroButton variant="secondary" size="l" onClick={openIntercom}>
            Get help from our Support team
          </FaroButton>
        )}

        <FaroButton variant="ghost" size="l" onClick={openHelpPage}>
          Learn more about 360 workflows
        </FaroButton>
      </Stack>
    </Stack>
  );
}
